import {
    Card,
    CardContent,
    CardActionArea,
    Box,
    Avatar,
    Stack,
    Typography,
    Badge,
} from "@mui/material";
import React from "react";
import {
    ContentGetItemResponse,
    ParamsGetWarehouse,
} from "../../constants/types";
import avatarAlt from "../../utils/avatarAlt";
import toRupiah from "../../utils/toRupiah";
import StarIcon from "@mui/icons-material/Star";
import { useTransaksi } from "../../context/transaksiContext";
import toRibuan from "../../utils/toRibuan";
import truncate from "../../utils/truncate";
import useUser from "../../services/queries/useUser";
import useWarehouse from "../../services/queries/useWarehouse";
import { getLocalWarehouseId } from "../../utils/handleChangeWarehouseId";

interface IMenuBarangProps {
    data: ContentGetItemResponse;
}

const MenuBarang = ({ data }: IMenuBarangProps) => {
    const {
        id,
        namaBarang,
        kategori,
        deskripsi,
        harga,
        gambar: url,
        sku,
        stok: jumlahStok,
        stokPusat,
        detailBundling,
        favorite,
        hpp,
        hargaMultiple,
    } = data;

    const namaKategori = kategori.nama;
    const multipleHarga = hargaMultiple.map((li) => ({
        id: li.id,
        harga: Number(li.harga),
        keterangan: li.keterangan,
    }));

    const { data: user } = useUser();

    const initialParamsWarehouse = {
        size: 500,
        page: 1,
        search: "",
    };

    const [paramsWarehouse, setParamsWarehouse] =
        React.useState<ParamsGetWarehouse>(initialParamsWarehouse);
    const { data: warehouse } = useWarehouse(paramsWarehouse);

    const warehouseId = React.useMemo(() => {
        if (user?.userId) {
            return getLocalWarehouseId(user?.userId);
        }
        return null;
    }, [user]);

    const warehouseData = React.useMemo(() => {
        if (warehouseId) {
            return warehouse?.content.find((li) => li.id === warehouseId);
        }
        return warehouse?.content[0];
    }, [warehouseId, warehouse]);

    const { addToCart, cart } = useTransaksi();

    React.useEffect(() => {
        if (user && user?.warehouseId?.length > 0) {
            setParamsWarehouse((prev) => ({
                ...prev,
                userId: user.warehouseId[0],
            }));
        }
    }, [user]);

    return (
        <Card
            variant="outlined"
            sx={{
                display: "flex",
                flexDirection: "column",
                position: "relative",
                borderColor:
                    warehouseData?.isDropship === 1
                        ? cart.findIndex((c) => c.id === id) >= 0 ||
                          (stokPusat < 0 && stokPusat !== null)
                            ? "primary.main"
                            : undefined
                        : cart.findIndex((c) => c.id === id) >= 0 ||
                          (jumlahStok < 0 && jumlahStok !== null)
                        ? "primary.main"
                        : undefined,
                borderWidth:
                    warehouseData?.isDropship === 1
                        ? cart.findIndex((c) => c.id === id) >= 0 ||
                          (stokPusat < 0 && stokPusat !== null)
                            ? "medium"
                            : undefined
                        : cart.findIndex((c) => c.id === id) >= 0 ||
                          (jumlahStok < 0 && jumlahStok !== null)
                        ? "medium"
                        : undefined,
            }}
        >
            <CardActionArea
                disabled={
                    warehouseData?.isDropship === 1
                        ? cart.findIndex((c) => c.id === id) >= 0 ||
                          (stokPusat < 0 && stokPusat !== null)
                            ? true
                            : false
                        : cart.findIndex((c) => c.id === id) >= 0 ||
                          (jumlahStok < 0 && jumlahStok !== null)
                        ? true
                        : false
                }
                onClick={() => {
                    addToCart(
                        {
                            id,
                            namaBarang,
                            namaKategori,
                            harga,
                            url,
                            qty: 1,
                            sku,
                            jumlahStok,
                            stokPusat,
                            detailBundling,
                            hpp,
                            faktur: [],
                            multipleHarga,
                        },
                        warehouseData?.isDropship === 1,
                    );
                }}
                sx={{
                    display: "flex",
                    flexDirection: "column",
                    justifyContent: "space-between",
                    height: "100%",
                }}
            >
                <CardContent sx={{ paddingBottom: 0, width: "100%" }}>
                    <Typography
                        fontSize={12}
                        color={
                            warehouseData?.isDropship === 1
                                ? stokPusat < 5 && stokPusat !== null
                                    ? "#ef4444"
                                    : "#76747C"
                                : jumlahStok < 5 && jumlahStok !== null
                                ? "#ef4444"
                                : "#76747C"
                        }
                        fontWeight={
                            warehouseData?.isDropship === 1
                                ? stokPusat < 5 && stokPusat !== null
                                    ? 700
                                    : 500
                                : jumlahStok < 5 && jumlahStok !== null
                                ? 700
                                : 500
                        }
                    >
                        {warehouseData?.isDropship === 1
                            ? detailBundling.length > 0 && stokPusat === null
                                ? "Otomatis"
                                : stokPusat === null
                                ? "Tanpa stok"
                                : stokPusat > 5
                                ? "Tersedia " + toRibuan(stokPusat) + " barang"
                                : stokPusat <= 5 && stokPusat > 0
                                ? "Barang tersisa " + toRibuan(stokPusat)
                                : "Barang telah habis"
                            : detailBundling.length > 0 && jumlahStok === null
                            ? "Otomatis"
                            : jumlahStok === null
                            ? "Tanpa stok"
                            : jumlahStok > 5
                            ? "Tersedia " + toRibuan(jumlahStok) + " barang"
                            : jumlahStok <= 5 && jumlahStok > 0
                            ? "Barang tersisa " + toRibuan(jumlahStok)
                            : "Barang telah habis"}
                    </Typography>
                    <Box display="flex" justifyContent="center">
                        <Badge
                            overlap="circular"
                            badgeContent={
                                favorite > 0 ? (
                                    <Box
                                        sx={{
                                            width: 32,
                                            height: 32,
                                            position: "relative",
                                            backgroundColor: "#fff",
                                            borderRadius: "50%",
                                        }}
                                    >
                                        <Box>
                                            <StarIcon
                                                htmlColor="#fbbf24"
                                                sx={{
                                                    fontSize: "1.3rem",
                                                    position: "absolute",
                                                    top: "50%",
                                                    left: "50%",
                                                    transform:
                                                        "translate(-50%, -50%)",
                                                }}
                                            />
                                        </Box>
                                    </Box>
                                ) : undefined
                            }
                            sx={{ marginY: 3 }}
                        >
                            <Avatar
                                alt={namaBarang}
                                src={url === null ? "" : url}
                                sx={{
                                    bgcolor: "primary.main",
                                    width: 70,
                                    height: 70,
                                }}
                            >
                                {avatarAlt(namaBarang)}
                            </Avatar>
                        </Badge>
                    </Box>
                    <Stack
                        direction="row"
                        spacing={2}
                        alignItems="center"
                        // marginBottom={2}
                        justifyContent="center"
                    >
                        <Typography
                            variant="h6"
                            textAlign="center"
                            fontWeight="bold"
                            fontSize="16px"
                        >
                            {namaBarang}
                        </Typography>
                    </Stack>
                    {deskripsi ? (
                        <Typography
                            variant="body2"
                            textAlign="center"
                            fontSize="12px"
                            color="#76747C"
                            sx={{ marginBottom: 2 }}
                        >
                            {truncate(deskripsi, 50)}
                        </Typography>
                    ) : undefined}
                </CardContent>
                {/* <CardContent sx={{ marginTop: "auto", bottom: "16px" }}> */}
                <Box display="flex" flexDirection="column" marginBottom={2}>
                    {multipleHarga.length > 0 ? (
                        <Box
                            display="flex"
                            flexDirection="row"
                            alignItems="center"
                            justifyContent="flex-end"
                            gap={0.5}
                        >
                            <Typography
                                variant="h6"
                                color="primary"
                                fontWeight="bold"
                                fontSize="18px"
                                textAlign="center"
                            >
                                {toRupiah(Number(multipleHarga[0].harga))}
                            </Typography>
                        </Box>
                    ) : (
                        <Typography
                            variant="h6"
                            color="primary"
                            fontWeight="bold"
                            fontSize="18px"
                            textAlign="center"
                            sx={{
                                marginBottom: 2,
                            }}
                        >
                            {toRupiah(harga)}
                        </Typography>
                    )}
                </Box>
                {/* </CardContent> */}
            </CardActionArea>
        </Card>
    );
};

export default MenuBarang;
